"use client";
import { useEffect, useRef } from "react";
import {
  Combine,
  KeyRound,
  GitBranch,
  Radio,
  ScrollText,
  Braces,
} from "lucide-react";

const CARDS = [
  {
    icon: Combine,
    title: "Alias clustering",
    body: "30 source rows for one person collapse into a single decision-ready card. No more spreadsheet archaeology.",
    viz: "alias",
  },
  {
    icon: KeyRound,
    title: "Strong-ID anchoring",
    body: "PAN, DIN, GSTIN, and DOB prevent two same-name people from ever being merged into one match.",
    viz: "ids",
  },
  {
    icon: GitBranch,
    title: "Multi-hop network walk",
    body: "4-hop BFS surfaces PEPs and sanctioned parties hiding up to 4 directors away from your customer.",
    viz: "network",
  },
  {
    icon: Radio,
    title: "Live data feeds",
    body: "Live GST and court records. Daily refresh for sanctions and wanted lists, monthly for MCA registers.",
    viz: "live",
  },
  {
    icon: ScrollText,
    title: "Audit-ready trail",
    body: "Every query, every decision, every dataset matched. Immutably logged with a one-click regulator export.",
    viz: "audit",
  },
  {
    icon: Braces,
    title: "Bulk + API",
    body: "10K-row CSV upload with live progress and resumable export. OpenAPI 3 REST for your existing KYC stack.",
    viz: "bulk",
  },
];

function Viz({ kind }) {
  switch (kind) {
    case "alias":
      return (
        <div className="viz-alias">
          <div className="rows">
            <span></span><span></span><span></span><span></span><span></span>
          </div>
          <div className="merged-card">
            <span className="label">MATCH CARD</span>
          </div>
        </div>
      );
    case "ids":
      return (
        <div className="viz-ids">
          <span>PAN</span>
          <span>DIN</span>
          <span>GSTIN</span>
          <span>DOB</span>
        </div>
      );
    case "network":
      return (
        <svg className="viz-network" viewBox="0 0 320 170" aria-hidden="true">
          <g className="net-edge">
            <path d="M40 130 L110 60" />
            <path d="M110 60 L190 100" />
            <path d="M190 100 L280 50" />
            <path d="M110 60 L60 110" />
            <path d="M190 100 L230 130" />
          </g>
          <path className="net-path" d="M40 130 L110 60 L190 100 L280 50" />
          <circle className="net-node" cx="40" cy="130" r="8" />
          <circle className="net-node" cx="110" cy="60" r="8" />
          <circle className="net-node" cx="190" cy="100" r="8" />
          <circle className="net-node-risk" cx="280" cy="50" r="9" />
          <circle className="net-node" cx="60" cy="110" r="5" opacity="0.5" />
          <circle className="net-node" cx="230" cy="130" r="5" opacity="0.5" />
        </svg>
      );
    case "live":
      return (
        <div className="viz-live">
          <div className="viz-live-dot"></div>
          <div className="viz-live-label">Live · sanctions · MCA · eCourts · GSTN</div>
        </div>
      );
    case "audit":
      return (
        <div className="viz-audit">
          <ul>
            <li><em>14:32:08</em> &nbsp;Screened <b>Rohan Verma</b></li>
            <li><em>14:32:11</em> &nbsp;Matched <b>UAPA · MHA</b></li>
            <li><em>14:32:14</em> &nbsp;Cleared <b>Priya C.</b></li>
            <li><em>14:32:18</em> &nbsp;Exported <b>audit.csv</b></li>
          </ul>
        </div>
      );
    case "bulk":
      return (
        <div className="viz-bulk">
          <div className="viz-bulk-row">
            <span className="label">SCREENING CSV</span>
            <span className="count">10,000 / 10,000</span>
          </div>
          <div className="viz-bulk-bar"><div></div></div>
          <div className="viz-bulk-api">POST <code>/api/v1/screen</code> · OpenAPI 3</div>
        </div>
      );
    default:
      return null;
  }
}

export default function FeaturesSection() {
  const stageRef = useRef(null);
  const containerRef = useRef(null);
  const cardRefs = useRef([]);

  useEffect(() => {
    const stage = stageRef.current;
    const container = containerRef.current;
    if (!stage || !container) return;
    const cards = cardRefs.current.filter(Boolean);
    if (cards.length < 2) return;

    const cardDistance = 50;
    const verticalDistance = 60;
    const skewAmount = 6;

    const placeCard = (card, opts) => {
      const { x, y, z, opacity, scale, zIndex } = opts;
      card.style.transform = `translate(-50%, -50%) translate3d(${x}px, ${y}px, ${z}px) skewY(${skewAmount}deg) scale(${scale})`;
      card.style.opacity = String(opacity);
      card.style.zIndex = String(zIndex);
    };

    // Initial placement: card 0 in front, others hidden below
    const setSnapshot = (idx) => {
      cards.forEach((card, i) => {
        if (i > idx) {
          placeCard(card, { x: 0, y: 480, z: -120, opacity: 0, scale: 0.92, zIndex: 1 });
        } else {
          const stackIdx = idx - i;
          placeCard(card, {
            x: stackIdx * cardDistance,
            y: -stackIdx * verticalDistance,
            z: -stackIdx * cardDistance * 1.5,
            opacity: 1,
            scale: 1,
            zIndex: cards.length - stackIdx,
          });
        }
        card.style.transition = "transform 0.85s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.7s ease-out";
      });
    };

    let activeIndex = 0;
    setSnapshot(activeIndex);

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const rect = stage.getBoundingClientRect();
        const scrollable = stage.offsetHeight - window.innerHeight;
        if (scrollable <= 0) {
          ticking = false;
          return;
        }
        const scrolledPast = Math.max(0, -rect.top);
        const progress = Math.max(0, Math.min(1, scrolledPast / scrollable));
        const target = Math.min(cards.length - 1, Math.floor(progress * cards.length));
        if (target !== activeIndex) {
          activeIndex = target;
          setSnapshot(activeIndex);
        }
        ticking = false;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <section ref={stageRef} className="features-sticky-stage bg-surface relative">
      <div className="features-sticky-inner">
        <div className="container-c grid w-full items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-blue">What makes RiskIntel smart</p>
            <h2 className="mt-3 font-display text-[36px] leading-[1.05] tracking-tightest text-balance text-ink sm:text-[44px] md:text-[56px]">
              More than a checklist tool.
            </h2>
            <p className="mt-5 max-w-md text-[17px] leading-relaxed text-muted">
              Six capabilities that separate RiskIntel from a basic checklist screen. Scroll to see each one stack.
            </p>
            <div className="mt-8">
              <a
                href="https://riskintel.cloudastra.co/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-12 items-center justify-center rounded-full bg-blue px-7 text-[15px] font-semibold text-white shadow-cta transition hover:bg-blue-hover"
              >
                Try Riskintel
              </a>
            </div>
          </div>

          <div className="relative" style={{ minHeight: "560px" }}>
            <div ref={containerRef} className="card-swap-container">
              {CARDS.map(({ icon: Icon, title, body, viz }, i) => (
                <div
                  key={title}
                  ref={(el) => (cardRefs.current[i] = el)}
                  className="swap-card"
                >
                  <div className="swap-card-viz">
                    <Viz kind={viz} />
                  </div>
                  <div className="swap-card-icon">
                    <Icon />
                  </div>
                  <h3 className="swap-card-title">{title}</h3>
                  <p className="swap-card-body">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
